import React, { useState, useMemo } from 'react';

// Re-export dev-drawer panel components so DenialCaseRunPage can import from
// either V4 or V5 without changing the developer drawer wiring.
export {
  S2_RootCause,
  S3_LookupTrace,
  S4_SourceGraphTrace,
  S6_DeterministicFindings,
  S7_SNNFindings,
  S8ext_PayerContradictions,
  S11_PacketOutputs,
  S12_HumanReviewFlags,
  S13_LearningTags,
  S13ext_LearningSignal,
  C6_AuditTrail,
} from './DenialResolveV4View';

const DASH = '\u2014';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toText(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') {
    // Detect Python-dict or JSON-object strings and extract body/text
    if (v.startsWith('{') && (v.includes("'body':") || v.includes('"body":'))) {
      const m = v.match(/['"]body['"]\s*:\s*['"]([\s\S]*?)(?:['"],\s*['"]|\}$)/);
      if (m) return m[1].slice(0, 500);
    }
    if (v.startsWith('{')) {
      try {
        const d = JSON.parse(v);
        if (typeof d === 'object' && d !== null) {
          return Object.entries(d).map(([k, val]) => `**${k}**: ${val}`).join('\n\n');
        }
      } catch (_e) { /* not JSON, return as-is */ }
    }
    return v;
  }
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map(toText).filter(Boolean).join('; ');
  if (typeof v === 'object') {
    if (v.denial_reason || v.contradicting_evidence || v.verbatim_source_clause) {
      const parts = [];
      if (v.denial_reason) parts.push(`Payer: ${toText(v.denial_reason)}`);
      if (v.contradicting_evidence) parts.push(`Evidence: ${toText(v.contradicting_evidence)}`);
      if (v.verbatim_source_clause) parts.push(`Source: "${toText(v.verbatim_source_clause)}"`);
      return parts.join(' | ');
    }
    if (v.body) return toText(v.body);
    if (v.argument || v.point) return toText(v.argument || v.point);
    if (v.name && v.purpose) return `${v.name} — ${v.purpose}`;
    if (v.name) return toText(v.name);
    if (v.text) return toText(v.text);
    if (v.label) return toText(v.label);
    try { return JSON.stringify(v); } catch (_e) { return String(v); }
  }
  return String(v);
}

function fmtCurrency(v) {
  if (v === null || v === undefined || v === '') return DASH;
  const n = Number(v);
  if (Number.isNaN(n)) return DASH;
  return n.toLocaleString(undefined, {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

function fmtPct(v) {
  if (v === null || v === undefined || v === '') return DASH;
  const n = Number(v);
  if (Number.isNaN(n)) return DASH;
  return n <= 1 ? `${Math.round(n * 100)}%` : `${Math.round(n)}%`;
}

function humanAction(action) {
  if (!action) return DASH;
  const map = {
    file_appeal: 'File Appeal', appeal: 'File Appeal',
    medical_necessity_appeal: 'File Appeal',
    submit_corrected_claim: 'Submit Corrected Claim',
    corrected_claim: 'Submit Corrected Claim',
    documentation_submission: 'Submit Documentation',
    clinical_review: 'Clinical Review',
    coder_review: 'Coder Review', coding_review: 'Coding Review',
    payer_follow_up: 'Payer Follow-up',
    cob_resubmission: 'COB Resubmission',
    auth_correction: 'Authorization Correction',
    underpayment_dispute: 'Dispute Underpayment',
    write_off: 'Write Off',
    patient_responsibility: 'Patient Responsibility',
    human_review: 'Human Review',
  };
  return map[action] || action.replace(/_/g, ' ');
}

function _copyToClipboard(text) {
  try { navigator?.clipboard?.writeText(text); } catch (_e) { /* best-effort */ }
}

function strengthBar(val) {
  const n = typeof val === 'number' ? val : parseFloat(val);
  if (!Number.isFinite(n)) return null;
  const pct = n <= 1 ? Math.round(n * 100) : Math.round(n);
  const tone = pct >= 70 ? 'good' : pct >= 40 ? 'warn' : 'bad';
  return (
    <div className="v5-strength" title={`${pct}%`}>
      <div className={`v5-strength__fill v5-strength__fill--${tone}`} style={{ width: `${pct}%` }} />
      <span className="v5-strength__label">{pct}%</span>
    </div>
  );
}

const NON_MITIGATION_ACTIONS = new Set([
  'human_review', 'human_review_required', 'write_off',
  'patient_responsibility', '', 'none',
]);

function _actionIsMitigation(action) {
  return action && !NON_MITIGATION_ACTIONS.has(String(action).toLowerCase());
}

function actionTone(action) {
  const a = String(action || '').toLowerCase();
  if (a === 'file_appeal' || a === 'appeal' || a === 'medical_necessity_appeal') return 'good';
  if (a === 'write_off' || a === 'human_review' || a === 'human_review_required') return 'bad';
  return 'warn';
}

function confTone(pct) {
  if (pct == null) return null;
  const n = Number(pct);
  const v = n <= 1 ? n * 100 : n;
  if (v >= 80) return 'good';
  if (v >= 50) return 'warn';
  return 'bad';
}

function priorityTone(p) {
  if (p === 'high') return 'bad';
  if (p === 'medium') return 'warn';
  return 'good';
}

function deriveActionOwner(action) {
  const a = String(action || '').toLowerCase();
  if (!a) return DASH;
  if (a === 'file_appeal' || a === 'appeal' || a === 'medical_necessity_appeal') return 'RCM Specialist';
  if (a.includes('corrected') || a === 'coder_review') return 'Coder';
  if (a === 'documentation_submission' || a === 'clinical_review') return 'Clinical Documentation';
  return 'Billing';
}

function deriveTotalCharged(caseFile) {
  const denials = Array.isArray(caseFile?.denial_payload?.denials) ? caseFile.denial_payload.denials : [];
  let total = 0, any = false;
  for (const d of denials) {
    if (!d) continue;
    const billed = Number(d.billed_amount ?? d.charge_amount ?? d.total_amount);
    if (Number.isFinite(billed) && billed > 0) { total += billed; any = true; }
  }
  if (any) return total;
  const gross = Number(caseFile?.financial_estimate?.gross_disputed);
  return (Number.isFinite(gross) && gross > 0) ? gross : null;
}

// ---------------------------------------------------------------------------
// 1. Verdict Header
// ---------------------------------------------------------------------------

function VerdictHeader({ caseFile }) {
  const ds = caseFile?.decision_summary || {};
  const fe = caseFile?.financial_estimate || {};
  const ra = caseFile?.recommended_action || {};
  const action = ra.action || ds.action;
  const isMitigation = _actionIsMitigation(action);
  const totalCharged = deriveTotalCharged(caseFile);
  const denied = Number(fe.denied_amount);
  const recover = Number(fe.expected_recovery);
  const recoveryPct = (Number.isFinite(denied) && denied > 0 && Number.isFinite(recover))
    ? Math.round((recover / denied) * 100) : null;
  const confidence = ds.final_confidence_pct != null
    ? ds.final_confidence_pct : (ds.confidence != null ? (Number(ds.confidence) <= 1 ? Math.round(Number(ds.confidence) * 100) : Math.round(Number(ds.confidence))) : null);
  const priority = toText(ds.priority) || 'medium';
  const letter = caseFile?.appeal_packet?.appeal_letter_text;

  return (
    <div className="v5-verdict">
      <div className="v5-verdict__left">
        <span className={`v5-verdict__action v5-verdict__action--${actionTone(action)}`}>
          {humanAction(action)}
        </span>
        {confidence != null && (
          <span className={`v5-verdict__pill v5-verdict__pill--${confTone(confidence)}`}>
            {confidence}% confidence
          </span>
        )}
        <span className={`v5-verdict__pill v5-verdict__pill--${priorityTone(priority)}`}>
          {priority} priority
        </span>
        <span className="v5-verdict__owner">{deriveActionOwner(action)}</span>
      </div>

      <div className="v5-verdict__center">
        {totalCharged != null && (
          <div className="v5-verdict__stat">
            <span className="v5-verdict__stat-label">Charged</span>
            <span className="v5-verdict__stat-value">{fmtCurrency(totalCharged)}</span>
          </div>
        )}
        <div className="v5-verdict__stat">
          <span className="v5-verdict__stat-label">Denied</span>
          <span className="v5-verdict__stat-value v5-verdict__stat-value--bad">{fmtCurrency(fe.denied_amount)}</span>
        </div>
        <div className="v5-verdict__stat v5-verdict__stat--hero">
          <span className="v5-verdict__stat-label">Recoverable</span>
          <span className="v5-verdict__stat-value v5-verdict__stat-value--good">{fmtCurrency(fe.expected_recovery)}</span>
        </div>
        {recoveryPct != null && (
          <div className="v5-verdict__stat">
            <span className="v5-verdict__stat-label">Recovery</span>
            <span className="v5-verdict__stat-value">{recoveryPct}%</span>
          </div>
        )}
      </div>

      <div className="v5-verdict__right">
        {isMitigation && letter && (
          <button type="button" className="denials-btn denials-btn--primary"
            onClick={() => _copyToClipboard(letter)}>
            Copy Appeal Letter
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Source Evidence Chunks — verbatim passages grouped by source document
// ---------------------------------------------------------------------------

function cleanGraphName(raw) {
  if (!raw) return 'SNN Graph';
  return raw
    .replace(/^mn_p\d+_/, '')
    .replace(/_snn$/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function fmtPages(pages) {
  if (!Array.isArray(pages) || pages.length === 0) return '';
  const nums = pages.map(p => (typeof p === 'object' ? (p.page || p.page_number || p) : p)).filter(Boolean);
  return nums.length > 0 ? `p.${nums.join(', ')}` : '';
}

function NodeEvidence({ nodes }) {
  const qualifying = useMemo(() => {
    if (!Array.isArray(nodes)) return [];
    return nodes
      .filter(n => {
        if (!n || !Array.isArray(n.citations) || n.citations.length === 0) return false;
        if (n.decision === 'No' && (n.score || 0) < 0.3) return false;
        return true;
      })
      .sort((a, b) => (b.score || 0) - (a.score || 0));
  }, [nodes]);

  if (qualifying.length === 0) return null;

  return (
    <div className="v5-node-evidence">
      {qualifying.map((node, ni) => (
        <div key={ni} className="v5-node-evidence__group">
          <div className="v5-node-evidence__header">
            <strong className="v5-node-evidence__graph-name">
              {cleanGraphName(node.graph_name || node.node_id)}
            </strong>
            {node.score != null && (
              <span className={`denials-pill denials-pill--${node.score >= 0.7 ? 'good' : node.score >= 0.4 ? 'warn' : 'neutral'}`}>
                {fmtPct(node.score)}
              </span>
            )}
          </div>
          <div className="v5-node-evidence__citations">
            {node.citations.map((cite, ci) => {
              const doc = (cite.source_doc || '').replace(/\.pdf$/i, '');
              const pages = fmtPages(cite.pages);
              const ref = cite.reference_text || cite.label || '';
              if (!doc && !ref) return null;
              return (
                <div key={ci} className="v5-node-evidence__cite">
                  <span className="v5-node-evidence__cite-num">[{cite.cite_num || ci + 1}]</span>
                  <span className="v5-node-evidence__cite-body">
                    {doc && <strong className="v5-node-evidence__doc">{doc}</strong>}
                    {pages && <span className="v5-node-evidence__pages">({pages})</span>}
                    {ref && <> {DASH} <span className="v5-node-evidence__ref-text">&ldquo;{ref}&rdquo;</span></>}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2. Evidence Chain — hero section
// ---------------------------------------------------------------------------

function EvidenceLineCard({ line, idx, defaultOpen, crosswalkRows, contradictions, graphTraces, snnNodeEvidence, deterministicFindings, snnFindings, checklist, locChainReasoning, rootCause }) {
  const [open, setOpen] = useState(defaultOpen);
  const carc = toText(line.carc || '').trim() || toText(line.denial_code_issue || '').split('/')[0]?.trim() || '';
  const rarc = toText(line.rarc || '').trim() || toText(line.denial_code_issue || '').split('/')[1]?.trim() || '';
  const payerReason = toText(line.payer_reason || line.payer_note || '');
  const lineNo = line.line_no ?? line.line;

  const relevantContradictions = useMemo(() => {
    if (!Array.isArray(contradictions)) return [];
    const byLine = contradictions.filter(c => c && c.claim_line_no != null && Number(c.claim_line_no) === Number(lineNo));
    return byLine.length > 0 ? byLine : contradictions;
  }, [contradictions, lineNo]);

  const topGraphTraces = useMemo(() => {
    if (!Array.isArray(graphTraces) || graphTraces.length === 0) return [];
    const normalized = graphTraces
      .map(t => {
        if (typeof t === 'string') {
          const text = t.trim();
          if (!text || text.includes('did not find relevant information')) return null;
          return { graph_name: 'SNN Graph Response', retrieved_clause: text, _isRaw: true };
        }
        return (t && (t.retrieved_clause || t.clause)) ? t : null;
      })
      .filter(Boolean);
    const sorted = normalized.sort((a, b) => (Number(b.relevance_score) || 0) - (Number(a.relevance_score) || 0));
    return sorted.slice(0, 8);
  }, [graphTraces]);

  const evidenceSummary = useMemo(() => {
    if (!Array.isArray(checklist) || checklist.length === 0) return null;
    const found = checklist.filter(e => String(e.found_status || '').toLowerCase() === 'found').length;
    const partial = checklist.filter(e => ['partial', 'conditional'].includes(String(e.found_status || '').toLowerCase())).length;
    const missing = checklist.filter(e => ['missing', 'not_found'].includes(String(e.found_status || '').toLowerCase())).length;
    return { found, partial, missing, total: checklist.length };
  }, [checklist]);

  const locChains = useMemo(() => {
    if (!locChainReasoning) return null;
    const fired = Array.isArray(locChainReasoning.fired_chains) ? locChainReasoning.fired_chains : [];
    if (fired.length === 0) return null;
    return { fired, coverageMet: locChainReasoning.coverage_met };
  }, [locChainReasoning]);

  return (
    <div className={`v5-evidence-card ${open ? 'v5-evidence-card--open' : ''}`}>
      <button type="button" className="v5-evidence-card__header" onClick={() => setOpen(!open)}>
        <div className="v5-evidence-card__header-left">
          <span className="v5-evidence-card__line">
            {lineNo != null ? `Line #${lineNo}` : `Finding #${idx + 1}`}
          </span>
          <span className="v5-evidence-card__codes">
            {carc && <code>{carc}</code>}
            {rarc && <> / <code>{rarc}</code></>}
          </span>
        </div>
        <div className="v5-evidence-card__header-right">
          {line.route && (
            <span className={`denials-pill denials-pill--${actionTone(line.route || line.action)}`}>
              {humanAction(line.route || line.action)}
            </span>
          )}
          <span className="v5-evidence-card__chevron">{open ? '\u25B2' : '\u25BC'}</span>
        </div>
      </button>

      {payerReason && (
        <div className="v5-evidence-card__payer-says">
          <strong>Payer says:</strong> {payerReason}
        </div>
      )}

      {open && (
        <div className="v5-evidence-card__body">
          {/* Denial Basis (Payer Rationale) */}
          {rootCause && rootCause.payer_rationale && (
            <div className="v5-evidence-card__section">
              <div className="v5-root-cause-block v5-root-cause-block--payer">
                <div className="v5-section-label">Denial Basis (Payer Rationale)</div>
                <p>{toText(rootCause.payer_rationale)}</p>
              </div>
            </div>
          )}

          {/* Denial Mitigation Analysis (deduplicated) */}
          {(() => {
            const cmText = toText(rootCause?.cm_interpretation || '');
            const specText = toText(line.specialist_interpretation || '');
            if (!cmText && !specText) return null;
            const isDup = cmText && specText && cmText.slice(0, 80) === specText.slice(0, 80);
            return (
              <div className="v5-evidence-card__mitigation-analysis">
                <div className="v5-section-label">Denial Mitigation Analysis</div>
                {isDup
                  ? <p>{cmText.length >= specText.length ? cmText : specText}</p>
                  : <>{cmText && <p>{cmText}</p>}{specText && <p>{specText}</p>}</>
                }
                {(() => {
                  const strong = crosswalkRows.filter(cw => cw.status === 'met' || cw.status === 'supported').slice(0, 3);
                  if (strong.length === 0) return null;
                  return (
                    <div className="v5-policy-basis">
                      <div className="v5-policy-basis__label">Policy basis:</div>
                      {strong.map((cw, i) => (
                        <div key={i} className="v5-policy-basis__row">
                          <strong>{toText(cw.topic || cw.policy_criterion || cw.source_name)}</strong>
                          <span className={`denials-pill denials-pill--${cw.status === 'met' ? 'good' : 'good'}`}>{cw.status}</span>
                          {cw.clinical_argument && <span className="v5-policy-basis__arg">{toText(cw.clinical_argument)}</span>}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            );
          })()}

          {/* Counter-arguments from policy crosswalk */}
          {crosswalkRows.length > 0 && (
            <div className="v5-evidence-card__section">
              <div className="v5-section-label">Policy Counter-Arguments</div>
              <div className="v5-crosswalk-cards">
                {crosswalkRows.map((cw, i) => (
                  <div key={i} className="v5-crosswalk-card">
                    <div className="v5-crosswalk-card__head">
                      <strong>{toText(cw.topic || cw.policy_criterion || `Criterion ${i + 1}`)}</strong>
                      {cw.status && (
                        <span className={`denials-pill denials-pill--${cw.status === 'met' || cw.status === 'supported' ? 'good' : cw.status === 'partial' ? 'warn' : 'bad'}`}>
                          {cw.status}
                        </span>
                      )}
                    </div>
                    {cw.source_name && (
                      <div className="v5-crosswalk-card__source">
                        Source: <strong>{toText(cw.source_name)}</strong>
                        {cw.source_trace_id && <span className="v5-muted"> ({toText(cw.source_trace_id)})</span>}
                      </div>
                    )}
                    {cw.policy_criterion && cw.topic && (
                      <div className="v5-crosswalk-card__criterion">
                        Criterion: {toText(cw.policy_criterion)}
                      </div>
                    )}
                    {Array.isArray(cw.relevant_case_evidence) && cw.relevant_case_evidence.length > 0 && (
                      <div className="v5-crosswalk-card__evidence">
                        <span className="v5-muted">Chart evidence:</span>{' '}
                        {cw.relevant_case_evidence.map(toText).join('; ')}
                      </div>
                    )}
                    {cw.clinical_argument && (
                      <div className="v5-crosswalk-card__argument">
                        {toText(cw.clinical_argument)}
                      </div>
                    )}
                    {cw.strength != null && (
                      <div className="v5-crosswalk-card__strength">
                        {strengthBar(cw.strength)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payer contradictions */}
          {relevantContradictions.length > 0 && (
            <div className="v5-evidence-card__section">
              <div className="v5-section-label">
                Payer Contradictions
                <span className="v5-section-count">{relevantContradictions.length}</span>
              </div>
              {relevantContradictions.map((c, i) => (
                <div key={i} className={`v5-contradiction ${c.contradiction_strength === 'strong' ? 'v5-contradiction--strong' : c.contradiction_strength === 'moderate' ? 'v5-contradiction--moderate' : 'v5-contradiction--weak'}`}>
                  <div className="v5-contradiction__row">
                    <span className="v5-contradiction__label">Payer asserts:</span>
                    <span>{toText(c.payer_assertion || c.denial_reason)}</span>
                  </div>
                  <div className="v5-contradiction__row">
                    <span className="v5-contradiction__label">Our evidence:</span>
                    <span>{toText(c.contradicting_evidence || c.cm_evidence)}</span>
                  </div>
                  {Array.isArray(c.policy_support) && c.policy_support.length > 0 && (
                    <div className="v5-contradiction__row">
                      <span className="v5-contradiction__label">Policy support:</span>
                      <span>{c.policy_support.map(toText).join('; ')}</span>
                    </div>
                  )}
                  <div className="v5-contradiction__footer">
                    <span className={`denials-pill denials-pill--${c.contradiction_strength === 'strong' ? 'good' : c.contradiction_strength === 'moderate' ? 'warn' : 'bad'}`}>
                      {toText(c.contradiction_strength)} contradiction
                    </span>
                    {c.appeal_argument_available && (
                      <span className="denials-pill denials-pill--good">appeal argument available</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* SNN citation sections removed — consolidated answer in Denial
              Mitigation Analysis is the authoritative analysis. Raw SNN
              citations were generic boilerplate from large reference docs. */}

          {/* LOC Chain Reasoning */}
          {locChains && (
            <div className="v5-evidence-card__section">
              <div className="v5-section-label">
                Level of Care Chains
                {locChains.coverageMet && <span className="denials-pill denials-pill--good">coverage met</span>}
              </div>
              <div className="v5-loc-chains">
                {locChains.fired.map((chain, i) => (
                  <div key={i} className="v5-loc-chain">
                    <code>{toText(chain.chain_id || chain)}</code>
                    {chain.graph_ref && <span className="v5-muted"> via {toText(chain.graph_ref)}</span>}
                    {chain.clinical_evidence && <span> — {toText(chain.clinical_evidence)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Deterministic Findings */}
          {Array.isArray(deterministicFindings) && deterministicFindings.length > 0 && (
            <div className="v5-evidence-card__section">
              <div className="v5-section-label">Other Checks</div>
              <div className="v5-det-cards">
                {deterministicFindings.map((f, i) => {
                  const criterion = toText(f.criterion_id || f.rule_id || f.criterion_text || f.rule_text);
                  const label = criterion.replace(/^cap_/, '').replace(/_v\d+$/, '').replace(/_/g, ' ');
                  const evUsed = Array.isArray(f.evidence_used) ? f.evidence_used : [];
                  const missing = Array.isArray(f.missing_evidence) ? f.missing_evidence : [];
                  const resultLabel = { unknown: 'Needs Review', needs_review: 'Needs Review', not_applicable: 'N/A' }[f.result] || toText(f.result);
                  const resultTone = f.result === 'pass' ? 'good' : f.result === 'fail' ? 'bad' : 'warn';
                  return (
                    <div key={i} className={`v5-det-card v5-det-card--${resultTone}`}>
                      <div className="v5-det-card__head">
                        <span className={`denials-pill denials-pill--${resultTone}`}>
                          {resultLabel}
                        </span>
                        <strong>{label}</strong>
                        {f.confidence != null && <span className="v5-muted">{fmtPct(f.confidence)} confidence</span>}
                      </div>
                      {evUsed.length > 0 && (
                        <div className="v5-det-card__evidence">
                          {evUsed.map((ev, j) => (
                            <div key={j} className="v5-det-card__ev-row">
                              {Object.entries(ev).filter(([k]) => k !== 'source').map(([k, v]) => (
                                <span key={k}><span className="v5-muted">{k.replace(/_/g, ' ')}:</span> {typeof v === 'boolean' ? (v ? 'Yes' : 'No') : toText(v)}</span>
                              ))}
                              {ev.source && <span className="v5-muted">Source: {toText(ev.source)}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                      {missing.length > 0 && (
                        <div className="v5-det-card__missing">
                          <span className="v5-muted">Missing:</span> {missing.map(toText).join(', ')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Evidence checklist — full item details */}
          {Array.isArray(checklist) && checklist.length > 0 && (
            <div className="v5-evidence-card__section">
              <div className="v5-section-label">
                Evidence Coverage
                {evidenceSummary && (
                  <span className="v5-section-count">
                    {evidenceSummary.found} found{evidenceSummary.partial > 0 ? `, ${evidenceSummary.partial} partial` : ''}{evidenceSummary.missing > 0 ? `, ${evidenceSummary.missing} missing` : ''} of {evidenceSummary.total}
                  </span>
                )}
              </div>
              <div className="v5-checklist-cards">
                {checklist.map((e, i) => {
                  const status = String(e.found_status || '').toLowerCase();
                  const tone = status === 'found' ? 'good' : (status === 'partial' || status === 'conditional') ? 'warn' : 'bad';
                  return (
                    <div key={i} className={`v5-checklist-card v5-checklist-card--${tone}`}>
                      <div className="v5-checklist-card__head">
                        <span className={`denials-pill denials-pill--${tone}`}>{e.found_status || 'Pending'}</span>
                        <strong>{toText(e.required_evidence)}</strong>
                        {e.strength != null && strengthBar(e.strength)}
                      </div>
                      {e.notes && (
                        <div className="v5-checklist-card__notes">{toText(e.notes)}</div>
                      )}
                      {e.why_it_matters && (
                        <div className="v5-checklist-card__why">
                          <span className="v5-muted">Why it matters:</span> {toText(e.why_it_matters)}
                        </div>
                      )}
                      {e.source && <div className="v5-muted">Source: {toText(e.source)}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EvidenceChain({ caseFile }) {
  const denialPayload = caseFile?.denial_payload || {};
  const denials = Array.isArray(denialPayload.denials) ? denialPayload.denials : [];
  const findingsUi = Array.isArray(caseFile?.denial_findings_mitigation_path_ui)
    ? caseFile.denial_findings_mitigation_path_ui : [];
  const correction = Array.isArray(caseFile?.correction_plan) ? caseFile.correction_plan : [];
  const crosswalkRows = Array.isArray(caseFile?.appeal_packet?.policy_crosswalk)
    ? caseFile.appeal_packet.policy_crosswalk : [];
  const contradictions = Array.isArray(caseFile?.payer_contradictions) ? caseFile.payer_contradictions : [];
  const graphTraces = Array.isArray(caseFile?.source_graph_trace) ? caseFile.source_graph_trace : [];
  const snnNodeEvidence = Array.isArray(caseFile?.snn_node_evidence) ? caseFile.snn_node_evidence : [];
  const deterministicFindings = Array.isArray(caseFile?.deterministic_findings) ? caseFile.deterministic_findings : [];
  const snnFindings = Array.isArray(caseFile?.snn_reasoning_findings) ? caseFile.snn_reasoning_findings : [];
  const checklist = Array.isArray(caseFile?.evidence_checklist) ? caseFile.evidence_checklist : [];
  const locChainReasoning = caseFile?.loc_chain_reasoning || null;
  const rootCause = caseFile?.root_cause || null;

  let lines;
  if (findingsUi.length > 0) {
    lines = findingsUi.map(d => ({
      line_no: d.line, line: d.line,
      carc: (d.denial_code_issue || '').split('/')[0]?.trim(),
      rarc: (d.denial_code_issue || '').split('/')[1]?.trim(),
      payer_reason: d.payer_reason,
      specialist_interpretation: d.specialist_interpretation,
      action: d.mitigation_action, route: d.route,
      supporting_evidence: d.supporting_evidence,
    }));
  } else if (correction.length > 0) {
    lines = correction.map(r => ({
      line_no: r.line_no, carc: r.carc, rarc: r.rarc,
      payer_reason: r.payer_reason || r.rationale,
      specialist_interpretation: r.rationale,
      action: r.action, route: r.action,
    }));
  } else {
    lines = denials.map(d => ({
      line_no: d.line_no, carc: d.carc, rarc: d.rarc,
      payer_reason: d.payer_note, action: 'human_review',
    }));
  }

  if (lines.length === 0) {
    return <div className="denials-subsection__empty">No denial findings available.</div>;
  }

  return (
    <div className="v5-evidence-chain">
      {lines.map((line, i) => (
        <EvidenceLineCard
          key={i}
          line={line}
          idx={i}
          defaultOpen={i === 0}
          crosswalkRows={crosswalkRows}
          contradictions={contradictions}
          graphTraces={graphTraces}
          snnNodeEvidence={snnNodeEvidence}
          deterministicFindings={deterministicFindings}
          snnFindings={snnFindings}
          checklist={checklist}
          locChainReasoning={locChainReasoning}
          rootCause={rootCause}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3. Appeal Package
// ---------------------------------------------------------------------------

function AppealPackage({ caseFile }) {
  const packet = caseFile?.appeal_packet || {};
  const pktOutputs = caseFile?.packet_outputs || {};
  const ra = caseFile?.recommended_action || {};
  const letter = toText(packet.appeal_letter_text);
  const keyArgs = Array.isArray(pktOutputs.key_arguments) ? pktOutputs.key_arguments
    : Array.isArray(packet.key_arguments) ? packet.key_arguments : [];
  const crosswalkRows = Array.isArray(packet.policy_crosswalk) ? packet.policy_crosswalk : [];
  const acRows = Array.isArray(packet.attachment_checklist) ? packet.attachment_checklist : [];
  const evRows = Array.isArray(packet.evidence_table) ? packet.evidence_table : [];
  const steps = Array.isArray(ra.steps) ? ra.steps : [];

  const attachmentRows = useMemo(() => {
    const rows = [];
    const seen = new Set();
    acRows.forEach(a => {
      const name = typeof a === 'object' ? (a.name || a.label) : a;
      const key = String(name || '').toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      rows.push({ name: toText(name), purpose: typeof a === 'object' ? toText(a.purpose || a.notes) : '', required: true });
    });
    evRows.forEach(e => {
      const name = toText(e.evidence) || toText(e.required_evidence);
      const key = String(name || '').toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      rows.push({ name, purpose: [toText(e.date), toText(e.fact_category)].filter(Boolean).join(' · '), required: false });
    });
    return rows;
  }, [acRows, evRows]);

  const anyContent = letter || keyArgs.length > 0 || crosswalkRows.length > 0
    || attachmentRows.length > 0 || steps.length > 0
    || packet.corrected_claim_instructions || packet.documentation_submission_instructions;

  if (!anyContent) {
    return <div className="denials-subsection__empty">No appeal package generated for this case.</div>;
  }

  return (
    <div className="v5-appeal-package">
      {/* Appeal Letter — open by default */}
      {letter && (
        <div className="v5-appeal-letter-section">
          <div className="v5-appeal-letter-header">
            <span className="v5-section-label">Appeal Letter Draft</span>
            <button type="button" className="denials-btn denials-btn--ghost"
              onClick={() => _copyToClipboard(letter)}>
              Copy
            </button>
          </div>
          <pre className="denials-letter">{letter}</pre>
        </div>
      )}

      {/* Corrected claim / documentation instructions */}
      {packet.corrected_claim_instructions && (
        <div className="v5-appeal-instructions">
          <div className="v5-section-label">Corrected Claim Instructions</div>
          <p>{toText(packet.corrected_claim_instructions)}</p>
        </div>
      )}
      {packet.documentation_submission_instructions && (
        <div className="v5-appeal-instructions">
          <div className="v5-section-label">Documentation Submission Instructions</div>
          <p>{toText(packet.documentation_submission_instructions)}</p>
        </div>
      )}

      {/* Key Arguments */}
      {keyArgs.length > 0 && (
        <div className="v5-key-arguments">
          <div className="v5-section-label">Key Arguments</div>
          <ol className="v5-key-arguments__list">
            {keyArgs.map((arg, i) => (
              <li key={i}>{toText(arg)}</li>
            ))}
          </ol>
        </div>
      )}

      {/* Enhanced Policy Crosswalk */}
      {crosswalkRows.length > 0 && (
        <details className="denials-disclosure" open>
          <summary>
            <strong>Policy Crosswalk</strong> · {crosswalkRows.length} clause{crosswalkRows.length === 1 ? '' : 's'}
          </summary>
          <div className="denials-disclosure__body">
            <table className="denials-table denials-table--wide">
              <thead>
                <tr>
                  <th>Topic</th>
                  <th>Policy Criterion</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Strength</th>
                </tr>
              </thead>
              <tbody>
                {crosswalkRows.map((p, i) => (
                  <tr key={i}>
                    <td><strong>{toText(p.topic || p.policy_graph_ref)}</strong></td>
                    <td className="denials-table__wrap">{toText(p.policy_criterion || p.clause)}</td>
                    <td>{toText(p.source_name) || DASH}</td>
                    <td>
                      {p.status && (
                        <span className={`denials-pill denials-pill--${p.status === 'met' || p.status === 'supported' ? 'good' : p.status === 'partial' ? 'warn' : 'bad'}`}>
                          {p.status}
                        </span>
                      )}
                    </td>
                    <td>{p.strength != null ? strengthBar(p.strength) : DASH}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {/* Attachments */}
      {attachmentRows.length > 0 && (
        <details className="denials-disclosure" open>
          <summary><strong>Required Attachments</strong> · {attachmentRows.length}</summary>
          <div className="denials-disclosure__body">
            <table className="denials-table">
              <thead><tr><th>Document</th><th>Type</th><th>Details</th></tr></thead>
              <tbody>
                {attachmentRows.map((a, i) => (
                  <tr key={i}>
                    <td>{a.name}</td>
                    <td>
                      <span className={`denials-pill denials-pill--${a.required ? 'warn' : 'good'}`}>
                        {a.required ? 'Required' : 'Recommended'}
                      </span>
                    </td>
                    <td className="denials-table__wrap">{a.purpose || DASH}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {/* Submission Instructions */}
      {steps.length > 0 && (
        <details className="denials-disclosure" open>
          <summary><strong>Submission Instructions</strong></summary>
          <div className="denials-disclosure__body">
            <ol className="denials-list__ol">
              {steps.map((s, i) => <li key={i}>{toText(s)}</li>)}
            </ol>
          </div>
        </details>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4. Case Context (compact, collapsed)
// ---------------------------------------------------------------------------

function CaseContext({ caseFile }) {
  const contextUi = Array.isArray(caseFile?.clinical_claim_context_ui)
    ? caseFile.clinical_claim_context_ui : [];
  const contextText = caseFile?.case_context;

  if (contextUi.length > 0) {
    return (
      <table className="denials-table denials-table--wide">
        <thead>
          <tr><th>Category</th><th>Detail</th></tr>
        </thead>
        <tbody>
          {contextUi.map((row, i) => (
            <tr key={i}>
              <td><strong>{toText(row.item || row.category || row.label)}</strong></td>
              <td className="denials-table__wrap">{toText(row.detail || row.value || row.text)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (contextText && typeof contextText === 'string') {
    const paragraphs = contextText.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
    return (
      <div className="denials-context-prose">
        {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
      </div>
    );
  }

  return <div className="denials-subsection__empty">No case context available.</div>;
}

// ---------------------------------------------------------------------------
// 5. Recommendation Rationale
// ---------------------------------------------------------------------------

function RecommendationRationale({ caseFile }) {
  const rc = caseFile?.root_cause || {};
  const ra = caseFile?.recommended_action || {};
  const ds = caseFile?.decision_summary || {};
  const loc = caseFile?.loc_chain_reasoning;

  const hasContent = rc.cm_interpretation || ra.rationale || ds.classification_rationale || loc;
  if (!hasContent) return <div className="denials-subsection__empty">No rationale available.</div>;

  return (
    <div className="v5-rationale">
      {rc.cm_interpretation && (
        <div className="v5-rationale__block">
          <div className="v5-section-label">CM Interpretation</div>
          <p>{toText(rc.cm_interpretation)}</p>
        </div>
      )}
      {rc.payer_rationale && (
        <div className="v5-rationale__block">
          <div className="v5-section-label">Payer Rationale</div>
          <p>{toText(rc.payer_rationale)}</p>
        </div>
      )}
      {ra.rationale && ra.rationale !== rc.cm_interpretation && (
        <div className="v5-rationale__block">
          <div className="v5-section-label">Recommendation Basis</div>
          <p>{toText(ra.rationale)}</p>
        </div>
      )}
      {loc && Array.isArray(loc.fired_chains) && loc.fired_chains.length > 0 && (
        <div className="v5-rationale__block">
          <div className="v5-section-label">
            Level of Care Chain Reasoning
            {loc.coverage_met && <span className="denials-pill denials-pill--good" style={{ marginLeft: 8 }}>coverage met</span>}
          </div>
          <div className="v5-loc-chains">
            {loc.fired_chains.map((chain, i) => (
              <div key={i} className="v5-loc-chain">
                <code>{toText(chain.chain_id || chain)}</code>
                {chain.graph_ref && <span className="v5-muted"> via {toText(chain.graph_ref)}</span>}
                {chain.clinical_evidence && <span> — {toText(chain.clinical_evidence)}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------

function V5Section({ id, title, defaultOpen, children }) {
  const [open, setOpen] = useState(defaultOpen !== false);
  return (
    <section className={`v5-section v5-section--${id}`}>
      <button type="button" className="v5-section__header" onClick={() => setOpen(!open)}>
        <span className="v5-section__title">{title}</span>
        <span className="v5-section__toggle">{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && <div className="v5-section__body">{children}</div>}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Top-level view
// ---------------------------------------------------------------------------

function DenialResolveV5View({ caseFile, loading, error, onRefresh }) {
  if (loading) {
    return (
      <div className="denials-processing-card">
        <div className="denials-processing-card__spinner" aria-hidden="true" />
        <div className="denials-processing-card__title">Loading Case File&hellip;</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="denials-processing-card denials-processing-card--error">
        <div className="denials-processing-card__title">Case File unavailable</div>
        <p className="denials-processing-card__text">{error}</p>
        {onRefresh && (
          <button type="button" className="denials-btn" onClick={onRefresh}>Try again</button>
        )}
      </div>
    );
  }
  if (!caseFile || typeof caseFile !== 'object' || Object.keys(caseFile).length === 0) {
    return (
      <div className="denials-processing-card">
        <div className="denials-processing-card__title">No Case File persisted yet</div>
        <p className="denials-processing-card__text">
          Run the case to generate the Denial Resolution Case File.
        </p>
      </div>
    );
  }

  return (
    <div className="v5-result">
      <VerdictHeader caseFile={caseFile} />

      <V5Section id="evidence" title="Evidence Chain" defaultOpen={true}>
        <EvidenceChain caseFile={caseFile} />
      </V5Section>

      <V5Section id="appeal" title="Appeal Package" defaultOpen={true}>
        <AppealPackage caseFile={caseFile} />
      </V5Section>

      <V5Section id="context" title="Case Context" defaultOpen={false}>
        <CaseContext caseFile={caseFile} />
      </V5Section>

      <V5Section id="rationale" title="Recommendation Rationale" defaultOpen={false}>
        <RecommendationRationale caseFile={caseFile} />
      </V5Section>
    </div>
  );
}

export default DenialResolveV5View;
